import { useState } from "react";

import { TFunction, useTranslation } from "@impero-com/common/utils/i18n";
import { queryClient } from "@impero-com/common/utils/query_client";
import { emitNotification, TextToolState } from "@impero-com/ui";
import { Row } from "@tanstack/react-table";

import {
    postControlResultsArchive,
    getGetControlResultsInfiniteQueryKey,
} from "api/generated/control-results/control-results";

import { ControlActivityTableRow } from "./types";

function computeArchiveToolState(
    t: TFunction,
    hasSelection: boolean,
    hasArchivedSelection: boolean,
    hasPermission: boolean
): TextToolState {
    if (!hasSelection) {
        return {
            type: "DISABLED",
            reason: t("ControlActivitiesList_Tooltip_NoSelection"),
        };
    }
    if (hasArchivedSelection) {
        return {
            type: "DISABLED",
            reason: t("ControlActivitiesList_Archive_Tooltip_Conflict"),
        };
    }
    if (!hasPermission) {
        return {
            type: "DISABLED",
            reason: t("ControlActivitiesList_Tooltip_Archive_NoPermission"),
        };
    }
    return { type: "ENABLED" };
}

interface UseControlActivityArchiveUtilsProps {
    selectedRows: Row<ControlActivityTableRow>[];
    deselectAll: () => void;
}

export function useControlActivityArchiveUtils({
    selectedRows,
    deselectAll,
}: UseControlActivityArchiveUtilsProps) {
    const { t } = useTranslation();
    const [archiveModalOpen, setArchiveModalOpen] = useState(false);
    const [isArchiving, setIsArchiving] = useState(false);

    const archivableIds = selectedRows
        .filter((row) => !row.original.archived && row.original.canEditProgram)
        .map((row) => row.original.activityId);

    const hasSelection = selectedRows.length > 0;
    const hasArchivedSelection = selectedRows.some(
        (row) => row.original.archived
    );
    const hasPermission = archivableIds.length > 0;

    const archiveToolState = computeArchiveToolState(
        t,
        hasSelection,
        hasArchivedSelection,
        hasPermission
    );

    const openArchiveModal = () => setArchiveModalOpen(true);
    const closeArchiveModal = () => setArchiveModalOpen(false);

    const handleArchive = async (trimmedComment: string) => {
        setIsArchiving(true);
        try {
            await postControlResultsArchive({
                controlResultIds: archivableIds,
                comment: trimmedComment || undefined,
            });
            // Archived activities drop out of the (non-archived) list, so refetch it.
            await queryClient.invalidateQueries({
                queryKey: getGetControlResultsInfiniteQueryKey(),
            });
            emitNotification(
                "success",
                t("ControlActivitiesList_Archive_Success", {
                    count: archivableIds.length,
                })
            );
            deselectAll();
            closeArchiveModal();
        } catch {
            emitNotification("error", t("UnknownError"));
        } finally {
            setIsArchiving(false);
        }
    };

    return {
        archiveToolState,
        archiveModalOpen,
        openArchiveModal,
        closeArchiveModal,
        handleArchive,
        isArchiving,
    };
}
